import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { withContent } from '@bubbletea/core';
import uniqueId from 'lodash/uniqueId';
import { useInView } from 'framer-motion';
import { IContent } from '../../../types';

import FAQItem from '../../Blocks/FAQItem';

import { AccordionSection, FAQContainer, FAQButtonContainer } from './styles';
import Title from '../../UI/Title';
import Button from '../../UI/Button';
import { ExternalIcon } from '../../UI/Icons';
import sendEvent from '../../../utils/sendEvent';
import { IStoreState } from '../../../types/store';

interface IFAQProps {
  context: IContent;
}

const FAQ = ({ context }: IFAQProps) => {
  const contentSelector = useSelector((state: IStoreState) => ({
    subscriptions: state.user.user.subscriptions,
    analyticsLoaded: state.app.analyticsLoaded,
    freeTrialDetected: state.windowVars.freeTrialDetected,
  }));
  const { subscriptions, analyticsLoaded, freeTrialDetected } = contentSelector;
  const [impressionEventRegistered, setImpressionEventRegistered] = useState(false);

  const subscriptionSuspended = subscriptions.some((subscription) => subscription.status === 'suspended');

  useEffect(() => {
    const impressionEventDetails = {
      location: 'faq',
      category: 'scroll',
      locationPosition: subscriptionSuspended ? 9 : 10,
      action: 'visible',
    };
    if (analyticsLoaded && impressionEventRegistered) {
      setImpressionEventRegistered(false);
      sendEvent(impressionEventDetails, 'impressionevent');
    }
  }, [impressionEventRegistered, analyticsLoaded]);

  const ref = useRef(null);
  const isInView = useInView(ref);
  const { faq } = context.googlesheet;
  const { title, faqItems, faqButton } = faq;

  const openFAQQuestion = (event: Event) => {
    const target = event.target as HTMLElement;
    const href = target.getAttribute('href');
    const regex = /#faq(\d+)/;
    const matches = href?.match(regex);
    if (matches?.length) {
      const openFAQQuestionEvent = new CustomEvent('OPEN_FAQ_QUESTION', {
        detail: matches[1],
      });
      document.dispatchEvent(openFAQQuestionEvent);
    }
  };

  useEffect(() => {
    document.addEventListener('click', openFAQQuestion);

    return () => {
      document.removeEventListener('click', openFAQQuestion);
    };
  }, []);

  const seeAllFAQ = () => {
    const eventDetails = {
      location: 'faq',
      category: 'action',
      locationPosition: subscriptionSuspended ? 9 : 10,
      action: 'see all faq',
    };
    sendEvent(eventDetails);
    if (faqButton.target !== '_self') {
      window.open(faqButton.link as string, faqButton.target as string);
    } else {
      window.location.href = faqButton.link as string;
    }
  };

  return (
    <FAQContainer
      id={'faq'}
      ref={ref}
      whileInView={'sendevent'}
      onAnimationStart={() => setImpressionEventRegistered(true)}
      viewport={{ once: true }}
    >
      <div className={'section-wrapper'}>
        <Title uppercase>{title}</Title>
        <AccordionSection>
          {faqItems.map((faqItem, index) => {
            if (faqItem.freeTrialFAQ && !freeTrialDetected) {
              return undefined;
            }
            return (
              (faqItem.enabled || faqItem.enabled === undefined) && (
                <FAQItem key={`faq_${uniqueId()}`} item={faqItem} index={index} isInView={isInView} />
              )
            );
          })}
        </AccordionSection>
        {faqButton.link ? (
          <FAQButtonContainer>
            <Button type={'outlined'} handleClick={() => seeAllFAQ()}>
              {faqButton.title}
              {faqButton.target !== '_self' ? <ExternalIcon /> : null}
            </Button>
          </FAQButtonContainer>
        ) : null}
      </div>
    </FAQContainer>
  );
};

export default withContent(FAQ);
